import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  Grid,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  ListItemText,
  Checkbox,
  Chip,
  Typography,
  IconButton,
  DialogTitle,
  InputAdornment,
  Divider
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import widgetApi from '../api/WidgetApi';

import topCarousels from '../resources/carousels_top.jpg';
import bodyCarousels from '../resources/carousels_body.jpg';
import footerCarousels from '../resources/carousels_footer.jpg';
import mediumCarousels from '../resources/carousels_medium.jpg';
import bottomCarousels from '../resources/carousels_bottom.jpg';
import {
  IDS_TYPE_MAP_BY_CAROUSEL_ACTION_TYPE,
  CAROUSELS_ACTION_TYPE_MAP,
  PRODUCT_IDS_TYPE_MAP,
  CAROUSELS_INDEX_START_FROM
} from '../constants';

import { useSnackbar } from 'notistack';

import {
    DAYS_OF_WEEK_MAP,
    IDS_TYPE_MAP_BY_FILTER_METHOD,
} from '../constants';
  
const useStyles = makeStyles((theme) => ({
  highlight: {
    animation: `$highlight 1s`,
  },
  imageContainer: {
    height: '155px',
    position: 'relative',
    overflow: 'hidden',
  },
  '@keyframes highlight': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.07)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));


const Carousel = ({ open, onUpdate, onClose, widget, token, countryCode }) => {
    const [errors, setErrors] = useState({ startHour: "", endHour: "" });
    const [selectedWidget, setSelectedWidget] = useState(null);
    const classes = useStyles();
    const [selectedDays, setSelectedDays] = useState(widget.param.days);
    const [image, setImageLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState(widget.param.user_tags || []);
    const [excludeUserTags, setExcludeUserTags] = useState(widget.param.exclude_user_tags || []);
    const [cityIds, setCityIds] = useState(widget.city_ids || []);
    const [mzIds, setMZIds] = useState(widget.microzone_ids || []);
    const [ids, setIds] = useState(widget.param.ids || []);
    const [userIds, setUserIds] = useState(widget.param.user_ids || []);
    const [enabled, setEnabled] = useState(widget.enabled);
    const [filterMethod, setFilterMethod] = useState(widget.param.filter_method);
    const [actionType, setActionType] = useState(widget.param.filter_method);
    const [listsType, setListsType] = useState(widget.param.ids_type || '');
    const [productIdsType, setProductIdsType] = useState(widget.param.product_ids_type || '');
    const { enqueueSnackbar } = useSnackbar();



    const positionRef = useRef();
    const carouselNameRef = useRef();
    const daysRef = useRef();
    const startDateRef = useRef();
    const endDateRef = useRef();
    const startHourRef = useRef();
    const elementsToShowRef = useRef();
    const maxItemsPerBrandRef = useRef();
    const endHourRef = useRef();
    const enabledRef = useRef();
    const filterMethodRef = useRef();
    const actionTypeRef = useRef();
    const listsTypeRef = useRef();
    const productIdsTypeRef = useRef();
    const completeDeeplinkRef = useRef();
    const ignoreCoverageRef = useRef();
    const deepLinkRef = useRef();
    const userTagsRef = useRef();
    const excludeUserTagsRef = useRef();
    const cityIdsRef = useRef();
    const mzIdsRef = useRef();
    const idsRef = useRef();
    const userIdsRef = useRef();
    const priorityRef = useRef();
    const deeplinkPlatformRef = useRef();

    const titleESRef = useRef();
    const titleENRef = useRef();
    const titlePTRef = useRef();

    const subTitleESRef = useRef();
    const subTitleENRef = useRef();
    const subTitlePTRef = useRef();

    const imageESRef = useRef();
    const imageENRef = useRef();
    const imagePTRef = useRef();

    const backgroundColorsRef = useRef();
    const lottieNameRef = useRef();
    const titleColorRef = useRef();
    const headerLogoRef = useRef();


    useEffect(() => {
        setSelectedWidget(widget);
    }, [widget]);

    useEffect(() => {
        if (listsType !== 'product_ids') {
            setProductIdsType('');
        }
    }, [listsType]);

  const handleSubmit = async () => {
    try {

      const backGroundColorsParsed = stringToArray(backgroundColorsRef.current.value)
      const position = parseInt(positionRef.current.value, 10);

      if (position < 1) {
        enqueueSnackbar('Error: The carousel position must be greater than 0', { variant: 'error' });
        return;
      }

      if (titleColorRef.current.value.length !== 7) {
        enqueueSnackbar('Error: The carousel must have a valid title color, a valid hexadecimal', { variant: 'error' });
        return;
      }

      if (ids.length === 0) {
        enqueueSnackbar('Error: The carousel must have at least one valid list', { variant: 'error' });
        return;
      }

      if (titleESRef.current.value.length === 0 || titleENRef.current.value.length === 0 || titlePTRef.current.value.length === 0) {
        enqueueSnackbar('Error: The carousel must have a valid ES, EN and PT title', { variant: 'error' });
        return;
      }

      if (!areAllIntegers(cityIds)) {
        enqueueSnackbar('Error: The city ids field must contain only integer numbers.', { variant: 'error' });
        return;
      }

      if (!areAllIntegers(mzIds)) {
        enqueueSnackbar('Error: The microzone ids field must contain only integer numbers.', { variant: 'error' });
        return;
      }

      if (imageESRef.current.value !== "") {
        if (!isValidImage(imageESRef.current.value)) {
          enqueueSnackbar('Error: The carousel must have a valid ES image', { variant: 'error' });
          return;
        }
  
        if (!isValidImage(imageENRef.current.value)) {
          enqueueSnackbar('Error: The carousel must have a valid EN image', { variant: 'error' });
          return;
        }
  
        if (!isValidImage(imagePTRef.current.value)) {
          enqueueSnackbar('Error: The carousel must have a valid PT image', { variant: 'error' });
          return;
        }
      }

      if (headerLogoRef.current.value !== "") {
        if (!isValidImage(headerLogoRef.current.value)) {
          enqueueSnackbar('Error: The carousel must have a valid header logo', { variant: 'error' });
          return;
        }
      }

      const updatedWidget = {
        ...widget,
        index: position + CAROUSELS_INDEX_START_FROM,
        title: titleESRef.current.value,
        sub_title: subTitleESRef.current.value,
        image: imageESRef.current.value,
        enabled: enabledRef.current.value,
        city_ids: cityIds,
        microzone_ids: mzIds,
        param: {
          ...widget.param,
          ids: ids,
          ids_type: listsType,
          product_ids_type: productIdsType,
          translations: {
            "es": {
              title: titleESRef.current.value,
              sub_title: subTitleESRef.current.value,
              image: imageESRef.current.value,
              lottie: lottieNameRef.current.value,
            },
            "en": {
              title: titleENRef.current.value,
              sub_title: subTitleENRef.current.value,
              image: imageENRef.current.value,
              lottie: lottieNameRef.current.value,
            },
            "pt": {
              title: titlePTRef.current.value,
              sub_title: subTitlePTRef.current.value,
              image: imagePTRef.current.value,
              lottie: lottieNameRef.current.value,
            }
          },
          filter_method: filterMethod,
          days: daysRef.current.value,
          start_hour: startHourRef.current.value,
          end_hour: endHourRef.current.value,
          start_date: startDateRef.current.value,
          end_date: endDateRef.current.value,
          user_ids: userIds,
          segmentation_ids: [],
          user_tags: tags,
          exclude_user_tags: excludeUserTags,
        },
        additional_information: {
          ...widget.additional_information,
          title_color: titleColorRef.current.value,
          lottie: lottieNameRef.current.value,
          background_colors: backGroundColorsParsed,
          header_logo: headerLogoRef.current.value,
        }
      }

      const response = await widgetApi.updateWidget(countryCode, updatedWidget, token)  
      enqueueSnackbar('Carousel saved successfully', { variant: 'success' });

      onUpdate(response);
      onClose();
    } catch (error) {
      console.log("ERR:", error)
      let errorMessage = 'Error updating carousel';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ': ' + error.response.data.message;
      } else {
        errorMessage += ': An unexpected error occurred';
      }
    
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDaysChange = (event) => {
    setSelectedDays(event.target.value);
    daysRef.current.value = event.target.value;
  };

  const handleTagKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setTags([...tags, event.target.value.trim()]);
      userTagsRef.current.value = '';
    }
  };

  const handleExcludeUserTagKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setExcludeUserTags([...excludeUserTags, event.target.value.trim()]);
      excludeUserTagsRef.current.value = '';
    }
  };

  const handleCityIdKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setCityIds([...cityIds, event.target.value.trim()]);
      cityIdsRef.current.value = '';
    }
  };

  const handleMZIdKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setMZIds([...mzIds, event.target.value.trim()]);
      mzIdsRef.current.value = '';
    }
  };

  const handleListKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setIds([...ids, event.target.value.trim()]);
      idsRef.current.value = '';
    }
  };

  const handleDeleteTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleDeleteExcludeUserTag = (tagToRemove) => {
    setExcludeUserTags(excludeUserTags.filter((tag) => tag !== tagToRemove));
  };

  const handleDeleteCityId = (cityIdToRemove) => {
    setCityIds(cityIds.filter((cityId) => cityId !== cityIdToRemove));
  };

  const handleDeleteMZId = (mzIdToRemove) => {
    setMZIds(mzIds.filter((mzId) => mzId !== mzIdToRemove));
  };

  const handleDeleteList = (listToRemove) => {
    setIds(ids.filter((list) => list !== listToRemove));
  };

  const handleKeyDownUserId = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setUserIds([...userIds, event.target.value.trim()]);
      userIdsRef.current.value = '';
    }
  };

  const handleDeleteUserId = (userIdToRemove) => {
    setUserIds(userIds.filter((userId) => userId !== userIdToRemove));
  };

  function stringToArray(inputString) {
    if (!inputString) {
      return [];
    }

    return inputString.trim().split(',').map(element => element.trim());
  }

  function arrayToString(inputArray) {
    if (!inputArray || !inputArray.length) {
      return '';
    }
    return inputArray.join(',');
  }

  const isValidImage = (imageUrl) => {
    return typeof imageUrl === 'string' && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'));
  };

  const areAllIntegers = (array) => {
    return array.every(element => Number.isInteger(Number(element)));
  }

  const handleChipClick = (id) => {
    navigator.clipboard.writeText(id).then(() => {
      enqueueSnackbar('List name copied to the clipboard.', { variant: 'default' });
    }).catch(err => {
      console.error("Error al copiar el texto al portapapeles", err);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle 
          style={{
            backgroundColor: widget.enabled ? '#e8f5e9' : '#d1d1d1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontFamily: 'Poppins, sans-serif',
            fontSize: '1.2rem',
            padding: '5.5px 20px',
        }}
      >
          {widget.enabled ? 'This carousel is enabled' : 'This carousel is disabled'}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12} md={6} style={{ marginTop: "5px" }}>
            <TextField
                label="ID"
                value={widget.id}
                margin="dense"
                disabled
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            edge="end"
                            onClick={() => {
                              navigator.clipboard.writeText(widget.id);
                              enqueueSnackbar('Carousel ID copied to the clipboard.', { variant: 'default' });
                          }}
                        >
                            <FileCopyIcon fontSize="small"/>
                        </IconButton>
                    </InputAdornment>
                  ),
                }}
            />
            <TextField
              label="Carousel Name"
              defaultValue={widget.name}
              inputProps={{ ref: carouselNameRef }}
              margin="dense"
              disabled
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                      <IconButton
                          edge="end"
                          onClick={() => {
                              navigator.clipboard.writeText(widget.name);
                              enqueueSnackbar('Carousel Name copied to the clipboard.', { variant: 'default' });
                          }}
                      >
                          <FileCopyIcon fontSize="small"/>
                      </IconButton>
                  </InputAdornment>
                ),
              }}
            />
             <TextField
              label="Carousel Position"
              type="number"
              defaultValue={ parseInt(widget.index, 10) - CAROUSELS_INDEX_START_FROM}
              inputProps={{ ref: positionRef }}
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <TextField
              label="Title ES"
              defaultValue={widget.param.translations['es']?.title || widget.title}
              inputProps={{ ref: titleESRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="SubTitle ES"
              defaultValue={widget.param.translations['es']?.sub_title || widget.sub_title}
              inputProps={{ ref: subTitleESRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Image ES"
              defaultValue={widget.param.translations['es']?.image || widget.image}
              inputProps={{ ref: imageESRef }}
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <TextField
              label="Title EN"
              defaultValue={widget.param.translations['en']?.title || widget.title}
              inputProps={{ ref: titleENRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="SubTitle EN"
              defaultValue={widget.param.translations['en']?.sub_title || widget.sub_title}
              inputProps={{ ref: subTitleENRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Image EN"
              defaultValue={widget.param.translations['en']?.image || widget.image}
              inputProps={{ ref: imageENRef }}
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <TextField
              label="Title PT"
              defaultValue={widget.param.translations['pt']?.title || widget.title}
              inputProps={{ ref: titlePTRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="SubTitle PT"
              defaultValue={widget.param.translations['pt']?.sub_title || widget.sub_title}
              inputProps={{ ref: subTitlePTRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Image PT"
              defaultValue={widget.param.translations['pt']?.image || widget.image}
              inputProps={{ ref: imagePTRef }}
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <TextField
              label="Title color"
              defaultValue={widget.additional_information.title_color || "#000000"}
              inputProps={{ ref: titleColorRef }}
              margin="dense"
              fullWidth
            />
          <TextField
              label="Background colors"
              defaultValue={arrayToString(widget.additional_information.background_colors) || ""}
              inputProps={{ ref: backgroundColorsRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Lottie name"
              defaultValue={widget.additional_information.lottie}
              inputProps={{ ref: lottieNameRef }}
              margin="dense"
              fullWidth
            />
            <TextField
              label="Header logo"
              defaultValue={widget.additional_information.header_logo}
              inputProps={{ ref: headerLogoRef }}
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  defaultValue={widget.param.start_date}
                  inputProps={{ ref: startDateRef }}
                  style={{
                    minWidth: '195px', 
                  }}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                </Grid>
                <Grid item xs={6}>
                <TextField
                  label="End date"
                  margin="normal"
                  type="date"
                  inputProps={{ ref: endDateRef }}
                  defaultValue={widget.param.end_date}
                  style={{
                    minWidth: '195px', 
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                </Grid>
            </Grid>
            <FormControl fullWidth margin="dense">
              <InputLabel  shrink={true} htmlFor="days-select">Days</InputLabel>
              <Select
                id="days-select"
                label="Days"
                multiple
                defaultValue={widget.param.days}
                inputProps={{ ref: daysRef }}
                onChange={handleDaysChange}
                renderValue={(selected) =>
                  selected.map((day) => DAYS_OF_WEEK_MAP[day]).join(', ')
                }
              >
                {Object.keys(DAYS_OF_WEEK_MAP).map((day) => (
                  <MenuItem key={day} value={day}>
                    <Checkbox checked={selectedDays.includes(day)} />
                    <ListItemText primary={DAYS_OF_WEEK_MAP[day]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Start hour"
                        defaultValue={widget.param.start_hour}
                        inputProps={{ ref: startHourRef }}
                        margin="dense"
                        fullWidth
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="End hour"
                        defaultValue={widget.param.end_hour}
                        inputProps={{ ref: endHourRef }}
                        margin="dense"
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <FormControl fullWidth margin="dense">
              <InputLabel shrink={true} htmlFor="action-select">Content</InputLabel>
              <Select
                id="action-select"
                label="Action"
                onChange={(e) => setFilterMethod(e.target.value)}
                defaultValue={widget.param.filter_method}
                inputProps={{ ref: filterMethodRef }}
              >
                {Object.keys(CAROUSELS_ACTION_TYPE_MAP).map((method) => (
                  <MenuItem key={method} value={method}>
                    <ListItemText primary={CAROUSELS_ACTION_TYPE_MAP[method]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>    
            <FormControl fullWidth margin="dense">
                <InputLabel shrink={true} htmlFor="list-type-select">List Type</InputLabel>
                <Select
                  id="list-type-select"
                  label="Lists Type"
                  defaultValue={widget.param.ids_type}
                  onChange={(e) => setListsType(e.target.value)}
                  inputProps={{ ref: listsTypeRef }}
                >
                  {Object.keys(IDS_TYPE_MAP_BY_CAROUSEL_ACTION_TYPE[filterMethod]).map((method) => (
                    <MenuItem key={method} value={method}>
                      <ListItemText primary={IDS_TYPE_MAP_BY_CAROUSEL_ACTION_TYPE[filterMethod][method]} />
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
              {listsType === 'product_ids' && (
            <FormControl fullWidth margin="dense">
                <InputLabel shrink={true} htmlFor="product-ids-type-select">Products Type</InputLabel>
                <Select
                    id="product-ids-type-select"
                    label="Products Type"
                    defaultValue={widget.param.product_ids_type}
                    onChange={(e) => setProductIdsType(e.target.value)}
                    inputProps={{ ref: productIdsTypeRef }}
               >
                   {Object.keys(PRODUCT_IDS_TYPE_MAP).map((type) => (
                       <MenuItem key={type} value={type}>
                           <ListItemText primary={PRODUCT_IDS_TYPE_MAP[type]} />
                       </MenuItem>
                   ))}
               </Select>
            </FormControl>
              )}
              <>
                {ids.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    onClick={() => handleChipClick(id)}
                    onDelete={() => handleDeleteList(id)}
                    style={{ marginRight: '5px', marginBottom: '5px' }}
                  />
                ))}
                <TextField
                  inputRef={idsRef}
                  label="Lists"
                  onKeyDown={handleListKeyDown}
                  placeholder="Add list"
                  style={{
                    minWidth: '195px', 
                  }}
                  margin="normal"
                  fullWidth
                />
              </>
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <div style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>User tags</Typography>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={userTagsRef}
              onKeyDown={handleTagKeyDown}
              placeholder="Add tag"
              margin="dense"
              fullWidth
            />
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>Exclude user tags</Typography>
            {excludeUserTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteExcludeUserTag(tag)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={excludeUserTagsRef}
              onKeyDown={handleExcludeUserTagKeyDown}
              placeholder="Add tag"
              margin="dense"
              fullWidth
            />
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>City Ids</Typography>
            {cityIds.map((cityId) => (
              <Chip
                key={cityId}
                label={cityId}
                onDelete={() => handleDeleteCityId(cityId)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={cityIdsRef}
              onKeyDown={handleCityIdKeyDown}
              placeholder="Add city Id"
              margin="dense"
              fullWidth
            />
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>Microzone Ids</Typography>
            {mzIds.map((mzId) => (
              <Chip
                key={mzId}
                label={mzId}
                onDelete={() => handleDeleteMZId(mzId)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={mzIdsRef}
              onKeyDown={handleMZIdKeyDown}
              placeholder="Add microzone Id"
              margin="dense"
              fullWidth
            />
          <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>Users ID (for test)</Typography>
            {userIds.map((userId) => (
              <Chip
                key={userId}
                label={userId}
                onDelete={() => handleDeleteUserId(userId)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}

            <TextField
              inputRef={userIdsRef}
              onKeyDown={handleKeyDownUserId}
              placeholder="Add User ID"
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
          </div>
              


<Grid container spacing={2}>
<Grid item xs={12}>
    <FormControl fullWidth margin="normal">
      <InputLabel htmlFor="status-select">
        Status
      </InputLabel>
      <Select
        id="status-select"
        defaultValue={widget.enabled}
        inputProps={{ ref: enabledRef }}
        label="Status"
      >
        <MenuItem value={true}>
  
          On
        </MenuItem>
        <MenuItem value={false}>
 
          Off
        </MenuItem>
      </Select>
    </FormControl>
  </Grid>
</Grid>
        </Grid>
        <Grid item xs={12} md={6}>
            <Stack direction="column" spacing={0}>
                <img
                src={topCarousels}
                alt="Widget"
                style={{
                    maxWidth: '100%',
                    maxHeight: '30%',
                    objectFit: 'contain',
                }}
                />
                <img
                src={bodyCarousels}
                alt="Widget"
                style={{
                    maxWidth: '100%',
                    maxHeight: '30%',
                    objectFit: 'contain',
                }}
                />

              <img
                src={bottomCarousels}
                alt="Widget"
                style={{
                    maxWidth: '100%',
                    maxHeight: '30%',
                    objectFit: 'contain',
                }}
                />
                                                <img
                src={mediumCarousels}
                alt="Widget"
                style={{
                    maxWidth: '100%',
                    maxHeight: '30%',
                    objectFit: 'contain',
                }}
                />

            </Stack>
        </Grid>
        </Grid>
        </DialogContent>
        <DialogActions>
  <Grid container spacing={2}>
    <Grid item xs={3}>
      <Button
        onClick={onClose}
        fullWidth
        style={{ backgroundColor: 'gray', color: 'white', width: 'calc(100% - 12px)', marginLeft: '15px' }}
      >
        Cancel
      </Button>
    </Grid>
    <Grid item xs={3}>
      <Button
        onClick={handleSubmit}
        fullWidth
        style={{ backgroundColor: '#00A86C', color: 'white', width: 'calc(100% - 12px)' }}
      >
        Save
      </Button>
    </Grid>

    <Grid item xs={6}></Grid> {/* Espacio vacío que ocupa la mitad del ancho restante */}
  </Grid>
</DialogActions>

    </Dialog>
    );
  };

export default Carousel;